import React, { useState, useEffect } from 'react';
import { getWebOrigin } from '../../utils/helpers';
import { CheckCircle, Users2, User, Plus, X, AlertCircle, Shield, Trash2, FileText } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { useAuth } from '../../contexts/AuthContext';
import { isEnabled, TOURNAMENT_ENTITIES, getDisabledReason } from '../access';
import { teamAPI } from '../../teams/services';
import { tournamentParticipantAPI } from '../services/tournamentApi';
import { paymentsAPI } from '../../payments/services/PaymentApi';
import { notificationAPI } from '../../services/api';
import { managedPlayersApi } from '../../profile/services/managedPlayersApi';
import TeamLogo from '../../teams/components/TeamLogo';
import ConfirmModal from '../../components/ConfirmModal';
import DocumentViewerModal from '../components/DocumentViewerModal';
import PaymentMethodSelector from '../../payments/components/PaymentMethodSelector';
import useStripeCheckout from '../../payments/services/PaymentStripeHooks';

const TEAMS_UI_PAGE_SIZE = 5;
const TEAMS_SERVER_BATCH_SIZE = 20;

function TermsWaiverAgreement({ tournament, registrationForm, handleRegistrationFormChange, canRegister, theme }) {
  const [viewer, setViewer] = useState(null); // 'terms' | 'waiver' | null

  return (
    <>
      {viewer && (
        <DocumentViewerModal
          type={viewer}
          tournamentId={tournament.id}
          onClose={() => setViewer(null)}
        />
      )}
      <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Terms & Conditions</h4>
        <div className="space-y-4">
          {tournament.requireTerms && (
            <div className={`flex items-start gap-x-3 ${canRegister ? '' : 'opacity-60 cursor-not-allowed'}`}>
              <input
                type="checkbox"
                checked={registrationForm.agreeToTerms}
                disabled={!canRegister}
                onChange={(e) => handleRegistrationFormChange('agreeToTerms', e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
              />
              <span className={`text-sm ${theme.text}`}>
                I agree to the tournament{' '}
                {tournament.termsContentPath ? (
                  <button
                    type="button"
                    onClick={() => setViewer('terms')}
                    className="text-blue-500 hover:text-blue-600 underline font-medium"
                  >
                    Terms &amp; Conditions
                  </button>
                ) : (
                  <span className="font-medium">Terms &amp; Conditions</span>
                )}
                , including rules, regulations, and code of conduct. *
              </span>
            </div>
          )}
          {tournament.requireWaiver && (
            <div className={`flex items-start gap-x-3 ${canRegister ? '' : 'opacity-60 cursor-not-allowed'}`}>
              <input
                type="checkbox"
                checked={registrationForm.agreeToWaiver}
                disabled={!canRegister}
                onChange={(e) => handleRegistrationFormChange('agreeToWaiver', e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
              />
              <span className={`text-sm ${theme.text}`}>
                I acknowledge and accept the{' '}
                {tournament.waiverContentPath ? (
                  <button
                    type="button"
                    onClick={() => setViewer('waiver')}
                    className="text-blue-500 hover:text-blue-600 underline font-medium"
                  >
                    Liability Waiver
                  </button>
                ) : (
                  <span className="font-medium">Liability Waiver</span>
                )}
                {' '}and understand the risks involved in participating in this tournament. *
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * RegisterTab Component
 *
 * Fully self-contained tournament registration form with team and individual registration.
 * Manages all registration state, team loading, and registration operations internally.
 *
 * @param {Object} props
 * @param {Object} props.tournament - Tournament data
 * @param {Object} props.tournamentVO - Tournament value object for access control
 * @param {Function} props.setActiveTab - Setter for active tab
 * @param {Function} props.loadParticipants - Function to reload participants after registration
 * @returns {JSX.Element}
 */
export default function RegisterTab({
  tournament,
  tournamentVO,
  setActiveTab,
  loadParticipants,
}) {
  const { theme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  // Present only on /academy/:slug/tournament/:id — i.e. an academy's own shell.
  const { slug: academySlug } = useParams();

  // Registration state
  const [isRegistered, setIsRegistered] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState({ type: '', message: '' });
  const [registrationType, setRegistrationType] = useState(null); // 'team', 'individual', or 'both'

  // Checkbox-based registration state
  const [isTeamRegistration, setIsTeamRegistration] = useState(false);
  const [isIndividualRegistration, setIsIndividualRegistration] = useState(false);
  const [individualRoles, setIndividualRoles] = useState({
    coowner: false,
    player: false,  // Will be set to true only for singles/singles_doubles tournaments
    coach: false,
    parents: false,
    teamOwner: false,
    referee: false,
    scorer: false
  });

  // Team selection state
  // `userTeams` accumulates in server-side batches of TEAMS_SERVER_BATCH_SIZE (enrolled-in-this-tournament
  // teams always sorted first, so they're never dropped by pagination); the UI itself pages through it
  // TEAMS_UI_PAGE_SIZE at a time, fetching another server batch once the buffer runs out.
  const [userTeams, setUserTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsUiPage, setTeamsUiPage] = useState(1);
  const [teamsServerPage, setTeamsServerPage] = useState(1);
  const [teamsHasMore, setTeamsHasMore] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState('all'); // 'all' | 'permanent' | 'temporary'

  // Store existing registration data for updates/withdrawal
  const [existingRegistrations, setExistingRegistrations] = useState([]);

  // Registration form state
  const [registrationForm, setRegistrationForm] = useState({
    teamName: '',
    teamSize: '',
    captainName: '',
    teamMembers: [],
    skillLevel: '',
    experience: '',
    dietaryRestrictions: '',
    medicalConditions: '',
    tshirtSize: '',
    agreeToTerms: false,
    agreeToWaiver: false
  });

  // Managed players state
  const [managedPlayers, setManagedPlayers] = useState([]);
  const [selectedManagedPlayerIds, setSelectedManagedPlayerIds] = useState([]);
  const [existingManagedPlayerRegs, setExistingManagedPlayerRegs] = useState({});

  /**
   * Has this payer got ANYTHING in this tournament already?
   *
   * `isRegistered` answers a narrower question than its name suggests: it comes
   * from checkRegistration for the signed-in user and their teams, and knows
   * nothing about the players they manage. So a parent whose eight children are
   * entered, and who has not entered themselves, read as not registered.
   *
   * Which sent unchecking a child down the REGISTER path, where removals do not
   * exist — and then into the capacity guard, because the tournament they were
   * trying to free a place in was full. The button did nothing, twice over.
   */
  const hasExistingEntries = isRegistered || Object.keys(existingManagedPlayerRegs).length > 0;
  const [managedPlayersLoading, setManagedPlayersLoading] = useState(false);

  // Local state for withdrawal confirmation modal

  // How the registrant intends to pay. One choice covers the whole submission;
  // each entry it creates carries it, since the fee is charged per entry.
  const [paymentMethod, setPaymentMethod] = useState(null);

  // Stripe has to return people to the shell they started in: an academy
  // tournament keeps the academy's chrome, a Kynaix one keeps Kynaix's.
  const registrationReturnPath = academySlug
    ? `/academy/${academySlug}/tournament/${tournament?.id}`
    : `/tournament/${tournament?.id}`;

  // Only the outbound half here — TournamentDetailPage handles the return,
  // because Stripe sends payers back to the Enrolled tab and this one will not
  // be mounted at that point.
  const { startCheckout } = useStripeCheckout();

  // Participant count state
  const [participantCount, setParticipantCount] = useState(0);
  const [participantCountLoading, setParticipantCountLoading] = useState(true);

  // Helper function to load and calculate actual participant count
  const refreshParticipantCount = async () => {
    if (!tournament?.id) return;

    try {
      // Fetch all participants to filter properly
      const response = await tournamentParticipantAPI.getByTournamentId(tournament.id, 1, 1000);
      const participants = response?.data || [];

      // Only count actual participants:
      // - Team registrations (teamId > 0)
      // - Individual registrations with player role (participantId is set AND player = true)
      const actualParticipantCount = participants.filter(p => {
        if (p.teamId && p.teamId > 0) return true;
        if (p.participantId && p.player) return true;
        return false;
      }).length;

      setParticipantCount(actualParticipantCount);
    } catch (error) {
      console.error('Failed to refresh participant count:', error);
    }
  };

  /**
   * Player is not a default. It is either true of somebody or it is not.
   *
   * A singles tournament used to switch this on the moment it loaded, for
   * everyone. So the box read as ticked for a person with no registration at
   * all — hidden while "Register as Individual" was unticked, and there
   * waiting, pre-answered, the moment they opened it. Worse, this effect fires
   * when the TOURNAMENT arrives, which can be after the registration has been
   * read: somebody registered as scorer alone was shown as a Player, and would
   * have been billed as one on the next save.
   *
   * What remains is not a default but a rule: a team-only tournament has no
   * individual player role, so the flag cannot stand there whatever was loaded.
   */
  useEffect(() => {
    const type = tournament?.participantsType;
    if (type && !['singles', 'singles_doubles'].includes(type)) {
      setIndividualRoles(prev => (prev.player ? { ...prev, player: false } : prev));
    }
  }, [tournament?.participantsType]);

  // Load participant count to check available openings
  useEffect(() => {
    const loadParticipantCount = async () => {
      setParticipantCountLoading(true);
      await refreshParticipantCount();
      setParticipantCountLoading(false);
    };

    loadParticipantCount();
  }, [tournament?.id]);

  // Check if user is already registered for this tournament
  useEffect(() => {
    const checkRegistration = async () => {
      if (!tournament?.id || !user?.id || !isAuthenticated) return;

      // Load managed players first
      setManagedPlayersLoading(true);
      let managedPlayersArr = [];
      try {
        const mpResult = await managedPlayersApi.getAll();
        managedPlayersArr = mpResult?.data || [];
        setManagedPlayers(managedPlayersArr);
      } catch (e) {
        console.error('Failed to load managed players:', e);
      } finally {
        setManagedPlayersLoading(false);
      }

      try {
        // First, load teams so we can check team registrations too
        let ownedTeams = [];
        try {
          ownedTeams = await fetchTeamsForTournament();
          setUserTeams(ownedTeams);
        } catch (error) {
          console.error('Failed to load teams:', error);
        }

        // Now check registrations — pass team IDs so we get both individual and team registrations
        const teamIds = ownedTeams.map(t => t.id);
        const existingRegistration = await tournamentParticipantAPI.checkRegistration(tournament.id, user.id, teamIds);
        const registrationData = existingRegistration?.data || existingRegistration || [];

        if (registrationData.length > 0) {
          setIsRegistered(true);
          setExistingRegistrations(registrationData);

          // Check if there are individual registrations (participantId is not null/0)
          const individualRegistrations = registrationData.filter(reg => reg.participantId);
          // Check if there are team registrations (teamId is not null/0)
          const teamRegistrations = registrationData.filter(reg => reg.teamId && reg.teamId > 0);

          // Set the registration type and related states based on the existing registration
          if (individualRegistrations.length > 0 && teamRegistrations.length > 0) {
            setIsTeamRegistration(true);
            setIsIndividualRegistration(true);
            setRegistrationType('both');
          } else if (teamRegistrations.length > 0) {
            setIsTeamRegistration(true);
            setRegistrationType('team');
          } else if (individualRegistrations.length > 0) {
            setIsIndividualRegistration(true);
            setRegistrationType('individual');
          }

          const registration = registrationData[0];

          // Set individual roles from boolean fields
          const rolesObj = {
            coowner: registration.coowner || false,
            player: registration.player || false,
            coach: registration.coach || false,
            parents: registration.parents || false,
            teamOwner: registration.teamOwner || false,
            referee: registration.referee || false,
            scorer: registration.scorer || false
          };
          setIndividualRoles(rolesObj);

          // ANY ROLE AT ALL MEANS THEY ARE REGISTERED AS AN INDIVIDUAL.
          //
          // The box above is ticked only when an INDIVIDUAL registration was
          // found, while these roles are read off registrationData[0] — which
          // may be a team row. So somebody could load this screen holding the
          // player role with "Register as Individual" unticked, and the roles
          // hidden underneath it: the form said they had chosen nothing while
          // the state said they were a player, and submitting would have
          // ignored the individual half entirely.
          //
          // Set here rather than only in the checkbox, so the two agree from
          // the moment the screen loads.
          if (Object.values(rolesObj).some(Boolean)) {
            setIsIndividualRegistration(true);
            setRegistrationType(prev => (prev === 'team' ? 'both' : 'individual'));
          }

          // Pre-check terms since the user already agreed during initial registration
          setRegistrationForm(prev => ({
            ...prev,
            agreeToTerms: true,
            agreeToWaiver: true
          }));

          // Load registered team details for the selection UI
          if (teamRegistrations.length > 0) {
            try {
              const teamDataPromises = teamRegistrations.map(reg => teamAPI.getById(reg.teamId));
              const teamResults = await Promise.all(teamDataPromises);
              const teams = teamResults.map(t => t?.data || t).filter(Boolean);
              setSelectedTeams(teams);
            } catch (error) {
              console.error('Failed to load registered teams:', error);
            }
          }
        }

        // Check which managed players are already registered
        if (managedPlayersArr.length > 0) {
          try {
            const allParticipantsResult = await tournamentParticipantAPI.getByTournamentId(tournament.id, 1, 1000);
            const allParticipants = allParticipantsResult?.data || [];
            const managedPlayerIds = new Set(managedPlayersArr.map(p => p.id));
            const managedRegs = {};
            allParticipants.forEach(p => {
              if (p.participantId && managedPlayerIds.has(p.participantId)) managedRegs[p.participantId] = p;
            });
            setExistingManagedPlayerRegs(managedRegs);
            setSelectedManagedPlayerIds(Object.keys(managedRegs));
          } catch (e) {
            console.error('Failed to check managed player registrations:', e);
          }
        }
      } catch (error) {
        console.error('Failed to check existing registration:', error);
      }
    };

    checkRegistration();
  }, [tournament?.id, user?.id, isAuthenticated]);

  // If the tournament is owned by a group, fetch that group's teams; otherwise fetch user's teams
  const isSquadTournament = tournament?.ownerType === 'group' || tournament?.owner_type === 'group';

  // Fetches one server-side batch (TEAMS_SERVER_BATCH_SIZE teams) of the caller's registerable
  // teams for this tournament: their permanent (user/group) teams unioned with teams created for
  // this tournament, with teams already enrolled in it always surfaced in the first batch regardless
  // of how many teams the caller owns.
  const fetchTeamsBatch = async (serverPage) => {
    const ownerType = isSquadTournament ? 'group' : 'user';
    const ownerId = isSquadTournament ? (tournament.ownerId || tournament.owner_id) : user.id;

    const result = await teamAPI.getRegisterableTeams(tournament.id, ownerType, ownerId, {
      page: serverPage,
      limit: TEAMS_SERVER_BATCH_SIZE,
      sportTypeId: tournament?.sportTypeId
    });
    const teams = (result?.data || []).map(team => ({
      ...team,
      _ownerCategory: team.ownerCategory || (team.ownerType === 'tournament' ? 'temporary' : 'permanent')
    }));

    setTeamsHasMore(!!result?.pagination?.hasNextPage);
    setTeamsServerPage(serverPage);
    return teams;
  };

  // Loads (or reloads) the first batch of registerable teams, resetting UI paging.
  const fetchTeamsForTournament = async () => {
    const teams = await fetchTeamsBatch(1);
    setTeamsUiPage(1);
    return teams;
  };

  // Function to load teams for tournament registration
  const loadUserTeams = async () => {
    if (!user?.id || !tournament) return;

    setTeamsLoading(true);
    try {
      const teams = await fetchTeamsForTournament();
      setUserTeams(teams);
    } catch (error) {
      console.error('Failed to load teams:', error);
      setUserTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  // Appends the next server-side batch to the buffer once the UI has paged through what's loaded.
  const loadMoreTeams = async () => {
    if (!teamsHasMore) return;
    setTeamsLoading(true);
    try {
      const moreTeams = await fetchTeamsBatch(teamsServerPage + 1);
      setUserTeams(prev => [...prev, ...moreTeams]);
    } catch (error) {
      console.error('Failed to load more teams:', error);
    } finally {
      setTeamsLoading(false);
    }
  };

  // Derive required player count range from tournament format.
  // Returns { min, max } or null if no requirement applies.
  const getPlayerRequirement = () => {
    if (tournament?.playersPerTeam && tournament.playersPerTeam > 0) {
      return { min: tournament.playersPerTeam, max: tournament.playersPerTeam };
    }
    if (tournament?.participantsType === 'doubles') {
      return { min: 2, max: 2 };
    }
    return null;
  };

  // Team selection handlers
  const handleSelectTeam = (team) => {
    // Check if already selected
    const isAlreadySelected = selectedTeams.some(t => t.id === team.id);
    if (isAlreadySelected) {
      // Allow deselection
      setSelectedTeams(prev => prev.filter(t => t.id !== team.id));
      return;
    }

    // Check for overlapping players with already-selected teams
    const getMemberId = (m) => m.userId || m.user_id || m.user?.id || m.id;
    const getMemberName = (m) => {
      const u = m.user || {};
      return `${u.firstName || u.first_name || m.firstName || m.first_name || ''} ${u.lastName || u.last_name || m.lastName || m.last_name || ''}`.trim() || 'Unknown';
    };
    const newMemberIds = new Set((team.members || []).map(getMemberId));
    for (const selected of selectedTeams) {
      const overlap = (selected.members || []).filter(m => newMemberIds.has(getMemberId(m)));
      if (overlap.length > 0) {
        const names = overlap.map(getMemberName).join(', ');
        toast.error(`Cannot select "${team.teamName || team.name}" — ${overlap.length > 1 ? 'players' : 'player'} ${names} already in "${selected.teamName || selected.name}".`);
        return;
      }
    }

    // TEMP: player-count requirement check disabled for testing — restore before merging
    // const req = getPlayerRequirement();
    // if (req) {
    //   const count = team.members?.length || 0;
    //   if (count < req.min || count > req.max) {
    //     const reqText = req.min === req.max ? `exactly ${req.min}` : `${req.min}–${req.max}`;
    //     toast.error(`This team has ${count} player${count === 1 ? '' : 's'}, but this format requires ${reqText} player${req.max === 1 ? '' : 's'} per team.`);
    //     return;
    //   }
    // }

    // Check if adding this team would exceed available openings
    if (tournament.maxParticipants && tournament.maxParticipants > 0) {
      // Calculate existing registrations by current user
      const existingTeamRegs = existingRegistrations.filter(reg => reg.teamId && reg.teamId > 0).length;
      const existingIndividualReg = existingRegistrations.filter(reg => reg.participantId && reg.player).length;

      // Calculate available openings excluding current user's existing registrations
      const otherParticipants = participantCount - existingTeamRegs - existingIndividualReg;
      const availableOpenings = tournament.maxParticipants - otherParticipants;

      // Calculate new selections (what user is trying to register now)
      const newSelections = selectedTeams.length + (isIndividualRegistration && individualRoles.player ? 1 : 0);

      if (newSelections >= availableOpenings) {
        toast.error(`Only ${availableOpenings} opening${availableOpenings === 1 ? '' : 's'} available. Cannot select more teams.`);
        return;
      }
    }

    // Add team to selection
    setSelectedTeams(prev => [...prev, team]);
  };

  const handleCreateNewTeam = () => {
    navigate('/create-team');
  };

  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);

  const participantTeamType = {
    singles: 'single',
    doubles: 'double',
    teams: 'team',
    singles_doubles: 'single',
  }[tournament?.participantsType] || 'team';

  const handleCreatePermanentTeams = () => {
    if (isSquadTournament) {
      const params = new URLSearchParams({
        ownerType: 'group',
        ownerId: tournament.ownerId,
        ownerLabel: tournament.ownerName || 'Squad',
        returnUrl,
        teamType: participantTeamType,
      });
      if (tournament.sportTypeId) params.set('sportTypeId', tournament.sportTypeId);
      if (tournament.eventId) params.set('eventId', tournament.eventId);
      navigate(`/create-team?${params}`);
    } else {
      const params = new URLSearchParams({ ownerType: 'user', returnUrl, teamType: participantTeamType });
      if (tournament.sportTypeId) params.set('sportTypeId', tournament.sportTypeId);
      if (tournament.eventId) params.set('eventId', tournament.eventId);
      navigate(`/create-team?${params}`);
    }
  };

  const handleCreateTemporaryTeams = () => {
    const params = new URLSearchParams({
      ownerType: 'tournament',
      ownerId: String(tournament.id),
      ownerLabel: tournament.tournamentName || tournament.tournamentname || 'Tournament',
      returnUrl,
      teamType: participantTeamType,
    });
    const groupId = tournament.ownerId || tournament.owner_id;
    if (isSquadTournament && groupId) params.set('memberGroupId', groupId);
    if (tournament.sportTypeId) params.set('sportTypeId', tournament.sportTypeId);
    if (tournament.eventId) params.set('eventId', tournament.eventId);
    navigate(`/create-team?${params}`);
  };

  const handleDeleteTemporaryTeam = async (team) => {
    try {
      await teamAPI.delete(team.id);
      setUserTeams(prev => prev.filter(t => t.id !== team.id));
      setSelectedTeams(prev => prev.filter(t => t.id !== team.id));
      toast.success(`"${team.teamName || team.name}" deleted.`);
    } catch (error) {
      toast.error('Failed to delete team. Please try again.');
    }
  };

  // Registration form handlers
  const handleRegistrationFormChange = (field, value) => {
    setRegistrationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Registration handlers
  const handleSubmitRegistration = async () => {
    // Validation
    // Only when this form is the one asking. A priced entry signs at checkout,
    // where the boxes actually exist — gating on them here would leave the
    // button permanently refusing an answer the payer was never offered.
    if (!hasFee && ((tournament?.requireTerms && !registrationForm.agreeToTerms) ||
        (tournament?.requireWaiver && !registrationForm.agreeToWaiver))) {
      setRegistrationStatus({
        type: 'error',
        message: 'Please agree to all required terms and conditions to continue.'
      });
      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 3000);
      return;
    }

    // Changing what is already there is an update, whoever it belongs to.
    if (hasExistingEntries) {
      await handleUpdateRegistration();
    } else {
      await handleRegisterForTournament();
    }
  };

  const handleRegisterForTournament = async () => {
    if (!isAuthenticated) {
      setRegistrationStatus({
        type: 'error',
        message: 'Please sign in to register for this tournament.'
      });
      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 3000);
      return;
    }

    if (!tournament || (!isTeamRegistration && !isIndividualRegistration && selectedManagedPlayerIds.length === 0)) {
      setRegistrationStatus({
        type: 'error',
        message: 'Please select a registration type.'
      });
      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 3000);
      return;
    }

    // TEMP: player-count requirement check disabled for testing — restore before merging
    // const req = getPlayerRequirement();
    // if (isTeamRegistration && req) {
    //   const invalidTeams = selectedTeams.filter(team => {
    //     const count = team.members?.length || 0;
    //     return count < req.min || count > req.max;
    //   });
    //
    //   if (invalidTeams.length > 0) {
    //     const teamNames = invalidTeams.map(t => t.teamName || t.name).join(', ');
    //     const reqText = req.min === req.max ? `${req.min}` : `${req.min}–${req.max}`;
    //     setRegistrationStatus({
    //       type: 'error',
    //       message: `The following team${invalidTeams.length > 1 ? 's do' : ' does'} not meet the player requirement (${reqText} players): ${teamNames}`
    //     });
    //     setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 5000);
    //     return;
    //   }
    // }

    // Check if tournament has reached maximum participants
    if (tournament.maxParticipants && tournament.maxParticipants > 0) {
      // Calculate existing registrations by current user
      const existingTeamRegs = existingRegistrations.filter(reg => reg.teamId && reg.teamId > 0).length;
      const existingIndividualReg = existingRegistrations.filter(reg => reg.participantId && reg.player).length;

      // Calculate available openings excluding current user's existing registrations
      const otherParticipants = participantCount - existingTeamRegs - existingIndividualReg;
      const availableOpenings = tournament.maxParticipants - otherParticipants;

      // Count new registrations (what user is trying to register now)
      const newRegistrationCount = (isTeamRegistration ? selectedTeams.length : 0) + (isIndividualRegistration && individualRoles.player ? 1 : 0);

      if (availableOpenings <= 0 && newRegistrationCount > existingTeamRegs + existingIndividualReg) {
        setRegistrationStatus({
          type: 'error',
          message: 'This tournament has reached maximum capacity. No more registrations are allowed.'
        });
        setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 5000);
        return;
      }

      if (newRegistrationCount > availableOpenings) {
        setRegistrationStatus({
          type: 'error',
          message: `Only ${availableOpenings} opening${availableOpenings === 1 ? '' : 's'} available. You are trying to register ${newRegistrationCount} participant${newRegistrationCount === 1 ? '' : 's'}.`
        });
        setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 5000);
        return;
      }
    }

    setRegistrationLoading(true);
    setRegistrationStatus({ type: '', message: '' });

    try {
      // Call the tournament-participants API
      // The API expects separate registrations for individuals and teams
      const registrations = [];

      if (isIndividualRegistration) {
        registrations.push(tournamentParticipantAPI.register({
          tournamentId: tournament.id,
          userId: user.id,
          roles: individualRoles,
          agreeToTerms: registrationForm.agreeToTerms,
          agreeToWaiver: registrationForm.agreeToWaiver,
          paymentMethod
        }));
      }

      if (isTeamRegistration && selectedTeams.length > 0) {
        selectedTeams.forEach(team => {
          registrations.push(tournamentParticipantAPI.register({
            tournamentId: tournament.id,
            teamId: team.id,
            agreeToTerms: registrationForm.agreeToTerms,
            agreeToWaiver: registrationForm.agreeToWaiver,
            paymentMethod
          }));
        });
      }

      const newManagedPlayerIds = selectedManagedPlayerIds.filter(id => !existingManagedPlayerRegs[id]);
      newManagedPlayerIds.forEach(playerId => {
        registrations.push(tournamentParticipantAPI.register({
          tournamentId: tournament.id,
          userId: playerId,
          roles: { player: true, coach: false, coowner: false, parents: false, teamOwner: false, referee: false, scorer: false },
          agreeToTerms: registrationForm.agreeToTerms,
          agreeToWaiver: registrationForm.agreeToWaiver,
          paymentMethod
        }));
      });

      if (registrations.length === 0) {
        throw new Error('No registration type selected or team not chosen');
      }

      // Execute all registrations
      const created = await Promise.all(registrations);

      // Card means leaving for Stripe. Do it before any success messaging —
      // the rows exist and hold the places, but nothing is paid yet, and
      // saying "registered!" as the page disappears would be a lie.
      //
      // Pays the ORDER, not the participant rows. Sent as kind:'tournament'
      // with participant ids, Stripe settled the legacy tournament_participants
      // columns and the order registration had raised stayed pending for ever —
      // so reporting, payouts and the Front Desk all saw an unpaid entry that
      // had in fact been paid.
      //
      // Deduped because one payer registering several entries for the same
      // tournament is one order with several lines, so every row hands back the
      // same id.
      if (hasFee && entriesAmong(created).length > 0) {
        const toPay = await ordersToPay(created);
        if (toPay.alreadyPaid) {
          toast.success('This entry is already paid — nothing further is owed.');
        } else if (toPay.ids.length) {
          // The checkout PAGE, not straight to stripe.com. Leaving from here
          // meant the payer came back to whichever tab they started on with no
          // way to retry, no total, and no other method if the card failed —
          // the same hole open play and programmes had. Entries are already
          // created by now, so all that is left is paying for them.
          // The entries just created, so abandoning checkout can undo them.
          // Without this the payer came back from a cancelled payment to find
          // themselves registered anyway — the tournament equivalent of open
          // play's held place, and the same fix: an explicit cancel withdraws
          // what this attempt created, and nothing it did not.
          const madeNow = (created || []).map(r => r?.id).filter(Boolean);
          // EVERY order this attempt raised, not just the first.
          //
          // One entrant is one order — that is what makes withdrawing a single
          // person possible later — so registering three people raises three.
          // Paying only `ids[0]` charged for one of them and left the other two
          // pending, while the payer was shown a receipt and told they were
          // done. Checkout settles the whole list under one charge.
          navigate(`/checkout?order=${toPay.ids.join(',')}`
            + `&from=${encodeURIComponent(`${registrationReturnPath}?tab=register`)}`
            + (madeNow.length ? `&release=tournament:${madeNow.join(',')}` : '')
            + (academySlug ? `&academy=${encodeURIComponent(academySlug)}` : '')
            + `&title=${encodeURIComponent('Tournament entry')}`);
          return;
        }
      }

      // Get selected roles for success message before resetting
      const selectedRoles = Object.entries(individualRoles)
        .filter(([, selected]) => selected)
        .map(([role]) => role);

      setRegistrationStatus({
        type: 'success',
        message: `Successfully registered for the tournament${registrationType === 'both' ? ' as both team and individual!' : registrationType === 'team' ? ' with your team!' : registrationType === 'individual' ? ' as ' + selectedRoles.join(', ') + '!' : '!'}`
      });

      // Transition to registered state — re-sync from server
      setIsRegistered(true);
      setRegistrationForm(prev => ({ ...prev, agreeToTerms: true, agreeToWaiver: true }));
      try {
        const teamIds = userTeams.map(t => t.id);
        const freshReg = await tournamentParticipantAPI.checkRegistration(tournament.id, user.id, teamIds);
        const freshData = freshReg?.data || freshReg || [];
        setExistingRegistrations(freshData);

        const freshIndividualRegs = freshData.filter(reg => reg.participantId);
        const freshTeamRegs = freshData.filter(reg => reg.teamId && reg.teamId > 0);
        setIsIndividualRegistration(freshIndividualRegs.length > 0);
        setIsTeamRegistration(freshTeamRegs.length > 0);

        if (freshIndividualRegs.length > 0 && freshTeamRegs.length > 0) setRegistrationType('both');
        else if (freshTeamRegs.length > 0) setRegistrationType('team');
        else if (freshIndividualRegs.length > 0) setRegistrationType('individual');

        if (freshIndividualRegs.length > 0) {
          const reg = freshIndividualRegs[0];
          setIndividualRoles({
            coowner: reg.coowner || false,
            player: reg.player || false,
            coach: reg.coach || false,
            parents: reg.parents || false,
            teamOwner: reg.teamOwner || false,
            referee: reg.referee || false,
            scorer: reg.scorer || false
          });
        }

        if (freshTeamRegs.length > 0) {
          const teamDataPromises = freshTeamRegs.map(reg => teamAPI.getById(reg.teamId));
          const teamResults = await Promise.all(teamDataPromises);
          setSelectedTeams(teamResults.map(t => t?.data || t).filter(Boolean));
        }

        if (managedPlayers.length > 0) {
          const allParticipantsResult = await tournamentParticipantAPI.getByTournamentId(tournament.id, 1, 1000);
          const allParticipants = allParticipantsResult?.data || [];
          const managedPlayerIds = new Set(managedPlayers.map(p => p.id));
          const managedRegs = {};
          allParticipants.forEach(p => {
            if (p.participantId && managedPlayerIds.has(p.participantId)) managedRegs[p.participantId] = p;
          });
          setExistingManagedPlayerRegs(managedRegs);
          setSelectedManagedPlayerIds(Object.keys(managedRegs));
        }
      } catch (e) {
        console.error('Failed to sync registration state after register:', e);
      }

      // Refresh enrolled participants list and participant count
      if (loadParticipants) {
        loadParticipants(1);
      }

      // Refresh participant count
      await refreshParticipantCount();

      // Create a notification for the tournament organizer (only if organizer exists)
      if (tournament.organizerUserId) {
        try {
          const registrationDetails = [];
          if (isTeamRegistration && selectedTeams.length > 0) {
            const teamNames = selectedTeams.map(t => t.teamName || t.name).join(', ');
            registrationDetails.push(`team${selectedTeams.length > 1 ? 's' : ''}: ${teamNames}`);
          }
          if (isIndividualRegistration && selectedRoles.length > 0) {
            registrationDetails.push(`roles: ${selectedRoles.join(', ')}`);
          }

          const message = `${user?.firstName || 'A user'} ${user?.lastName || ''} has registered for your tournament: ${tournament.tournamentName}${registrationDetails.length > 0 ? ` (${registrationDetails.join(' and ')})` : ''}`;

          await notificationAPI.create({
            message,
            userId: tournament.organizerUserId,
            webLink: `${getWebOrigin()}/tournament/${tournament.id}`,
            priority: 'normal',
            createdBy: user?.id || 1
          });
        } catch (notificationError) {
          console.error('Failed to send registration notification:', notificationError);
        }
      }

      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 5000);
    } catch (error) {
      console.error('Failed to register for tournament:', error);
      setRegistrationStatus({
        type: 'error',
        message: error.message || 'Failed to register for tournament. Please try again.'
      });
      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 5000);
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleUpdateRegistration = async () => {
    if (!isAuthenticated) {
      setRegistrationStatus({
        type: 'error',
        message: 'Please sign in to update your registration.'
      });
      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 3000);
      return;
    }

    if (!tournament || (!isTeamRegistration && !isIndividualRegistration && selectedManagedPlayerIds.length === 0)) {
      setRegistrationStatus({
        type: 'error',
        message: 'Please select a registration type to update.'
      });
      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 3000);
      return;
    }

    setRegistrationLoading(true);
    setRegistrationStatus({ type: '', message: '' });

    try {
      // First, delete existing registrations that don't match current selection
      /**
       * WITHDRAWALS ARE QUEUED, NOT FIRED.
       *
       * Held as functions rather than started promises, so they run one after
       * another below. Several entrants share a Stripe charge, and refunding
       * one is a partial refund against it — issued all at once they raced each
       * other on the same charge and the payer was shown failures for money
       * that had in fact gone back.
       *
       * Sequential costs a few hundred milliseconds on the rare multi-removal
       * and takes the race away entirely.
       */
      const withdrawals = [];
      const updatePromises = [];
      const createPromises = [];
      // An existing registration that becomes a player owes the entry fee, so
      // it joins the newly created ones at checkout.
      let upgradedToPlayerId = null;

      // Check what registrations already exist vs what's currently selected
      const existingIndividualRegs = existingRegistrations.filter(reg => reg.participantId);
      const existingTeamRegs = existingRegistrations.filter(reg => reg.teamId && reg.teamId > 0);

      // Handle individual registration updates
      if (isIndividualRegistration) {
        if (existingIndividualRegs.length > 0) {
          // Taking on the player role turns a non-paying registration into an
          // entry. The server prices it; the payment method has to travel with
          // the change, and the registration then needs paying for like any
          // other — otherwise scoring a tournament and then ticking Player
          // gets you a free place.
          const existingIndividual = existingIndividualRegs[0];
          if (!existingIndividual.player && individualRoles.player && hasFee) {
            upgradedToPlayerId = existingIndividual.id;
          }

          updatePromises.push(
            tournamentParticipantAPI.updateRegistration(existingIndividual.id, {
              coowner: individualRoles.coowner || false,
              player: individualRoles.player || false,
              coach: individualRoles.coach || false,
              parents: individualRoles.parents || false,
              teamOwner: individualRoles.teamOwner || false,
              referee: individualRoles.referee || false,
              scorer: individualRoles.scorer || false,
              ...(upgradedToPlayerId ? { paymentMethod } : {})
            })
          );
        } else {
          // Create new individual registration
          createPromises.push(
            tournamentParticipantAPI.register({
              tournamentId: tournament.id,
              userId: user.id,
              roles: individualRoles,
              agreeToTerms: registrationForm.agreeToTerms,
              agreeToWaiver: registrationForm.agreeToWaiver,
              paymentMethod
            })
          );
        }
      } else {
        // Delete existing individual registrations if individual is not selected
        existingIndividualRegs.forEach(reg => {
          withdrawals.push(() => tournamentParticipantAPI.withdrawRegistration(reg.id));
        });
      }

      // Handle team registration updates
      if (isTeamRegistration && selectedTeams.length > 0) {
        const selectedTeamIds = selectedTeams.map(t => t.id);
        const existingTeamIds = existingTeamRegs.map(reg => reg.teamId);

        // Delete team regs that are no longer selected
        existingTeamRegs.forEach(reg => {
          if (!selectedTeamIds.includes(reg.teamId)) {
            withdrawals.push(() => tournamentParticipantAPI.withdrawRegistration(reg.id));
          }
        });

        // Create new team regs for newly selected teams
        selectedTeams.forEach(team => {
          if (!existingTeamIds.includes(team.id)) {
            createPromises.push(
              tournamentParticipantAPI.register({
                tournamentId: tournament.id,
                teamId: team.id,
                agreeToTerms: registrationForm.agreeToTerms,
                agreeToWaiver: registrationForm.agreeToWaiver,
                paymentMethod
              })
            );
          }
        });
      } else {
        // Delete all existing team registrations if team is not selected
        existingTeamRegs.forEach(reg => {
          withdrawals.push(() => tournamentParticipantAPI.withdrawRegistration(reg.id));
        });
      }

      // Managed player adds (newly selected, not yet registered)
      const newManagedPlayerIds = selectedManagedPlayerIds.filter(id => !existingManagedPlayerRegs[id]);
      newManagedPlayerIds.forEach(playerId => {
        createPromises.push(tournamentParticipantAPI.register({
          tournamentId: tournament.id,
          userId: playerId,
          roles: { player: true, coach: false, coowner: false, parents: false, teamOwner: false, referee: false, scorer: false },
          agreeToTerms: registrationForm.agreeToTerms,
          agreeToWaiver: registrationForm.agreeToWaiver,
          paymentMethod
        }));
      });
      // Managed player removes (previously registered, now deselected)
      const deselectedManagedPlayerIds = Object.keys(existingManagedPlayerRegs)
        .filter(id => !selectedManagedPlayerIds.includes(id));
      deselectedManagedPlayerIds.forEach(playerId => {
        withdrawals.push(() => tournamentParticipantAPI.withdrawRegistration(existingManagedPlayerRegs[playerId].id));
      });

      // Execute all operations. The update results are kept rather than
      // discarded: taking on the player role raises the entry's order, and its
      // id comes back on that PATCH.
      // One at a time, and before anything else: a place given up should be
      // free for the next line to take.
      for (const withdraw of withdrawals) await withdraw();

      const [updatedNow, ...createdNow] = await Promise.all([
        Promise.all(updatePromises),
        ...createPromises,
      ]);

      // Adding entries to an existing registration owes money just the same,
      // so the card handoff belongs here too — not only on first registration.
      //
      // Orders, not participant ids: see the first registration path above for
      // what paying the legacy rows instead left behind.
      // Only when this attempt actually ADDED an entry. Unchecking a player
      // owes nothing — it is the opposite — but the old condition asked only
      // whether an unpaid order existed, so removing someone sent the payer to
      // checkout as though they were registering.
      //
      // Removing a PAID entry is a refund, not a checkout, and there is no
      // refund path yet: it withdraws and the money is settled by hand.
      const addedNow = (createdNow || []).filter(Boolean);
      // Or an existing registration that has just taken the player role —
      // scoring a tournament and then ticking Player owes the fee like anyone.
      if (hasFee && (entriesAmong(addedNow).length > 0 || upgradedToPlayerId)) {
        const toPay = await ordersToPay([...createdNow, ...(updatedNow || [])]);
        if (toPay.alreadyPaid) {
          toast.success('This entry is already paid — nothing further is owed.');
        } else if (toPay.ids.length) {
          // Only what THIS attempt added. Entries that already existed are not
          // this payment's to withdraw.
          const madeNow = [...(createdNow || [])].map(r => r?.id).filter(Boolean);
          // EVERY order this attempt raised, not just the first.
          //
          // One entrant is one order — that is what makes withdrawing a single
          // person possible later — so registering three people raises three.
          // Paying only `ids[0]` charged for one of them and left the other two
          // pending, while the payer was shown a receipt and told they were
          // done. Checkout settles the whole list under one charge.
          navigate(`/checkout?order=${toPay.ids.join(',')}`
            + `&from=${encodeURIComponent(`${registrationReturnPath}?tab=register`)}`
            + (madeNow.length ? `&release=tournament:${madeNow.join(',')}` : '')
            + (academySlug ? `&academy=${encodeURIComponent(academySlug)}` : '')
            + `&title=${encodeURIComponent('Tournament entry')}`);
          return;
        }
      }

      // Update registration type based on current selection
      let newRegistrationType = null;
      if (isTeamRegistration && isIndividualRegistration) {
        newRegistrationType = 'both';
      } else if (isTeamRegistration) {
        newRegistrationType = 'team';
      } else if (isIndividualRegistration) {
        newRegistrationType = 'individual';
      }

      setRegistrationType(newRegistrationType);

      // Get selected roles for success message
      const selectedRoles = Object.entries(individualRoles)
        .filter(([, selected]) => selected)
        .map(([role]) => role);

      const successMsg = `Successfully updated your registration${newRegistrationType === 'both' ? ' for both team and individual participation!' : newRegistrationType === 'team' ? ' for your team!' : newRegistrationType === 'individual' ? ` as ${selectedRoles.join(', ')}!` : '!'}`;
      setRegistrationStatus({ type: 'success', message: successMsg });
      toast.success(successMsg);

      // Refresh registration data and sync all UI state from server
      const teamIds = userTeams.map(t => t.id);
      const updatedRegistration = await tournamentParticipantAPI.checkRegistration(tournament.id, user.id, teamIds);
      const updatedRegistrationData = updatedRegistration?.data || updatedRegistration || [];
      setExistingRegistrations(updatedRegistrationData);
      setIsRegistered(true);
      setRegistrationForm(prev => ({ ...prev, agreeToTerms: true, agreeToWaiver: true }));

      const syncedIndividualRegs = updatedRegistrationData.filter(reg => reg.participantId);
      const syncedTeamRegs = updatedRegistrationData.filter(reg => reg.teamId && reg.teamId > 0);
      setIsIndividualRegistration(syncedIndividualRegs.length > 0);
      setIsTeamRegistration(syncedTeamRegs.length > 0);

      if (syncedIndividualRegs.length > 0 && syncedTeamRegs.length > 0) setRegistrationType('both');
      else if (syncedTeamRegs.length > 0) setRegistrationType('team');
      else if (syncedIndividualRegs.length > 0) setRegistrationType('individual');

      if (syncedIndividualRegs.length > 0) {
        const reg = syncedIndividualRegs[0];
        setIndividualRoles({
          coowner: reg.coowner || false,
          player: reg.player || false,
          coach: reg.coach || false,
          parents: reg.parents || false,
          teamOwner: reg.teamOwner || false,
          referee: reg.referee || false,
          scorer: reg.scorer || false
        });
      }

      if (syncedTeamRegs.length > 0) {
        try {
          const teamDataPromises = syncedTeamRegs.map(reg => teamAPI.getById(reg.teamId));
          const teamResults = await Promise.all(teamDataPromises);
          setSelectedTeams(teamResults.map(t => t?.data || t).filter(Boolean));
        } catch (e) {}
      } else {
        setSelectedTeams([]);
      }

      // Re-fetch managed player registrations
      if (managedPlayers.length > 0) {
        try {
          const allParticipantsResult = await tournamentParticipantAPI.getByTournamentId(tournament.id, 1, 1000);
          const allParticipants = allParticipantsResult?.data || [];
          const managedPlayerIds = new Set(managedPlayers.map(p => p.id));
          const managedRegs = {};
          allParticipants.forEach(p => {
            if (p.participantId && managedPlayerIds.has(p.participantId)) managedRegs[p.participantId] = p;
          });
          setExistingManagedPlayerRegs(managedRegs);
          setSelectedManagedPlayerIds(Object.keys(managedRegs));
        } catch (e) {
          console.error('Failed to refresh managed player registrations:', e);
        }
      }

      if (loadParticipants) {
        loadParticipants(1);
      }

      // Refresh participant count
      await refreshParticipantCount();

      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 5000);

      // On a successful update, take the user to Enrolled so they can see
      // their updated registration reflected there, scrolled to the top
      // rather than wherever they happened to be on this (long) form.
      setActiveTab('enrolled');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to update registration:', error);
      const errorMsg = error.message || 'Failed to update registration. Please try again.';
      setRegistrationStatus({ type: 'error', message: errorMsg });
      toast.error(errorMsg);
      setTimeout(() => setRegistrationStatus({ type: '', message: '' }), 5000);
    } finally {
      setRegistrationLoading(false);
    }
  };

  // Use access control if VO is available
  const canRegister = tournamentVO
    ? isEnabled(user?.id, TOURNAMENT_ENTITIES.REGISTRATION_ENABLE, tournamentVO)
    : (tournament?.status !== 'completed' && tournament?.status !== 'in_progress');

  const canWithdraw = tournamentVO
    ? isEnabled(user?.id, TOURNAMENT_ENTITIES.REGISTRATION_WITHDRAW, tournamentVO)
    : (tournament?.status !== 'completed' && tournament?.status !== 'in_progress');

  const registrationDisabledReason = tournamentVO
    ? getDisabledReason(TOURNAMENT_ENTITIES.REGISTRATION_ENABLE, tournamentVO)
    : (tournament?.status === 'in_progress'
        ? 'Tournament has started. Registration is locked.'
        : 'Registration is not available');

  // Calculate available openings
  const maxParticipants = tournament?.maxParticipants || 0;
  // Count managed players that are newly selected (checked but not yet registered)
  const pendingManagedCount = selectedManagedPlayerIds.filter(id => !existingManagedPlayerRegs[id]).length;
  // Count teams selected but not yet submitted
  const pendingTeamCount = selectedTeams.filter(t => !existingRegistrations.some(r => r.teamId === t.id)).length;
  // Count individual registration if newly selected (not yet submitted)
  const pendingIndividualCount = (
    isIndividualRegistration &&
    individualRoles.player &&
    !existingRegistrations.some(r => r.participantId && r.player)
  ) ? 1 : 0;
  const availableOpenings = maxParticipants > 0
    ? maxParticipants - participantCount - pendingManagedCount - pendingTeamCount - pendingIndividualCount
    : null;
  const isFull = availableOpenings !== null && availableOpenings <= 0;

  /* Payment. The fee is charged per entry, so a submission that creates three
   * rows owes three fees — the same counts that drive capacity tell us how many
   * entries are about to be created.
   *
   * ⚠️ READ FROM THE TAG, not `registrationFee`. That column is what the form
   * used to write and NO payment path reads: `PaymentSellablesService
   * .tournament()` prices an entry from `tournaments.price_id` and refuses an
   * untagged entry outright. Reading the column let this screen quote a fee
   * checkout would not charge — or quote nothing while checkout charged. The
   * column is kept as the fallback only for a tournament whose fee has not been
   * turned into a tag yet. */
  const entryFee = Number(tournament?.price?.amount ?? tournament?.registrationFee ?? 0);
  const hasFee = entryFee > 0;

  /**
   * What card checkout should actually settle, in every state an entry can be in.
   *
   * Choosing card did nothing at all — no Stripe, no message, a form that looked
   * like it had worked. Three different states all produced an empty list, and
   * each needs a different answer:
   *
   *  1. A fresh entry hands back its order id. Easy, and the only case the first
   *     version handled.
   *  2. Re-submitting an entry that already exists raises nothing, so no id comes
   *     back. The order it already has must be found and paid.
   *  3. An entry with NO order — registered before payments moved to orders, or
   *     one whose order was cancelled — has nothing to pay yet, so one has to be
   *     raised. The server prices it; we only name the subject.
   *
   * The old code never hit any of this because it paid PARTICIPANT ids, which
   * always exist. It always reached Stripe, and settled the wrong row.
   *
   * @returns {{ids: string[]} | {alreadyPaid: true}}
   */
  /**
   * WHAT ACTUALLY OWES THE FEE.
   *
   * A place in the draw: a player, or a team. Organiser, coach, referee,
   * scorer, parent, team owner are jobs — somebody turning up to run the thing
   * is not entering it, and the server has always agreed (`isEntry` decides
   * whether an order is raised at all).
   *
   * The client did not agree. Registering as scorer alone created the entry,
   * the server correctly raised no order, and this screen then found no order,
   * fell through to raising one itself, and sent a volunteer to checkout for
   * twenty dollars.
   */
  const entriesAmong = (responses) => (responses || [])
    .filter(r => r && (r.player === true || r.teamId || r.team_id));

  const ordersToPay = async (responses) => {
    const fromResponses = [...new Set(responses.map(r => r?.orderId).filter(Boolean))];
    if (fromResponses.length) {
      // A reused order may already be settled — registration is idempotent and
      // hands back what exists, paid or not. Charging again is the one outcome
      // worse than doing nothing.
      const states = await Promise.all(
        fromResponses.map(id => paymentsAPI.getOrder(id).catch(() => null)),
      );
      const unpaid = states.filter(o => o && o.status === 'pending').map(o => o.id);
      if (unpaid.length) return { ids: unpaid };
      if (states.some(o => o?.status === 'paid')) return { alreadyPaid: true };
    }

    const existing = await paymentsAPI.liveOrderFor('tournament', tournament.id);
    if (existing) return { ids: [existing.id] };

    /* Nothing owed yet for an entry that predates orders. Raise the bill so it
     * can be paid, rather than leaving a fee that no screen can collect.
     *
     * The entrant is the signed-in payer: this path only runs for their own
     * entry, which is why liveOrderFor above searches on nothing else. Named
     * explicitly because the server refuses a line that does not say — null
     * means "my account", and absent means nobody decided. */
    const raised = await paymentsAPI.startBooking({
      items: [{ subjectType: 'tournament', subjectId: String(tournament.id), playerId: null }],
    });
    return { ids: raised?.id ? [raised.id] : [] };
  };

  const entryCount = pendingManagedCount + pendingTeamCount + pendingIndividualCount;
  const feeLabel = hasFee
    ? `$${(entryFee * Math.max(entryCount, 1)).toFixed(2).replace(/\.00$/, '')}`
    : null;
  // Nothing new to pay for means nothing to ask — reopening the tab on an
  // existing registration shouldn't demand a payment method again.
  const needsPaymentChoice = hasFee && entryCount > 0;
  // Nothing to be missing any more: the method is chosen at checkout, so this
  // form has no answer to wait for. Left as it was, removing the picker would
  // have disabled the submit button on every priced tournament for ever —
  // an unanswerable question blocking the only way forward.
  const paymentMissing = false;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-xl font-bold ${theme.text}`}>Register for Tournament</h3>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>
            {registrationType === 'both' ? 'Team & Individual Registration' :
             registrationType === 'team' ? 'Team Registration' :
             registrationType === 'individual' ? 'Individual Registration' :
             'Choose your registration type'} • {tournament?.tournamentName}
          </p>
        </div>
        <div className="flex items-center gap-x-3">
          {/* Available Openings Indicator */}
          {maxParticipants > 0 && (
            <div className={`px-4 py-2 rounded-lg ${
              participantCountLoading
                ? `${theme.backgroundSecondary} ${theme.textMuted}`
                : isFull
                ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                : availableOpenings <= 5
                ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
                : 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'
            }`}>
              {participantCountLoading ? (
                <div className="flex items-center gap-x-2">
                  <div className={`w-4 h-4 border-2 ${theme.borderSecondary} border-t-transparent rounded-full animate-spin`}></div>
                  <span className="text-sm font-medium">Loading...</span>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {isFull ? '0' : availableOpenings}
                  </div>
                  <div className="text-xs">
                    {isFull ? 'FULL' : `opening${availableOpenings === 1 ? '' : 's'} left`}
                  </div>
                  <div className="text-xs opacity-75 mt-0.5">
                    {participantCount}/{maxParticipants} registered
                  </div>
                </div>
              )}
            </div>
          )}
          {isRegistered && (
            <div className={`flex items-center gap-x-2 px-4 py-2 ${theme.backgroundSecondary} ${theme.link} rounded-lg`}>
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Already Registered</span>
            </div>
          )}
        </div>
      </div>

      {/* Tournament Completed/Registration Disabled/Full Notice */}
      {(!canRegister || isFull) && isAuthenticated && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-6 border-2 ${isFull && !isRegistered ? 'border-red-500' : 'border-yellow-500'}`}>
          <div className="flex items-center gap-x-3">
            <AlertCircle className={`w-6 h-6 ${isFull && !isRegistered ? 'text-red-500' : 'text-yellow-500'}`} />
            <div>
              <h4 className={`text-lg font-semibold ${theme.text}`}>
                {isFull && !isRegistered
                  ? 'Tournament Full'
                  : isRegistered
                  ? 'Registration Locked'
                  : 'Registration Unavailable'}
              </h4>
              <p className={`text-sm ${theme.textMuted} mt-1`}>
                {isFull && !isRegistered
                  ? 'This tournament has reached its maximum capacity. No more registrations are allowed.'
                  : isRegistered
                  ? `${registrationDisabledReason}. Your registration is shown below in read-only mode.`
                  : registrationDisabledReason
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Registration Type Selection */}
      {/* ALWAYS SHOWN TO SOMEONE SIGNED IN.
          It used to be hidden once a singles tournament filled up, which took
          away the only screen showing what you had registered — and the only
          way to give a place back. A full tournament is exactly when somebody
          needs to look at their entry.
          What may be DONE here is decided per control, and already was: a new
          entry is disabled when there is no room, while one that already exists
          stays unlockable so it can be unchecked, which is how a place is
          withdrawn. Hiding the container overrode all of that. */}
      {isAuthenticated && (
        <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
          <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Registration Type</h4>
          <div className="space-y-4">
            {/* Team Registration Checkbox */}
            {(['teams', 'doubles', 'singles_doubles'].includes(tournament?.participantsType)) && (
              <div className="space-y-4">
                <label className={`flex items-center gap-x-3 ${canRegister && !isFull ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={isTeamRegistration}
                    disabled={!canRegister || (isFull && !isRegistered)}
                    onChange={async (e) => {
                      setIsTeamRegistration(e.target.checked);
                      if (e.target.checked) {
                        // Load user teams when checkbox is checked
                        if (loadUserTeams) {
                          await loadUserTeams();
                        }
                      } else {
                        setSelectedTeams([]);
                        setUserTeams([]);
                      }

                      // Update registration type based on current selections
                      if (e.target.checked && isIndividualRegistration) {
                        setRegistrationType('both');
                      } else if (e.target.checked) {
                        setRegistrationType('team');
                      } else if (isIndividualRegistration) {
                        setRegistrationType('individual');
                      } else {
                        setRegistrationType(null);
                      }
                    }}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-x-2">
                    <Users2 className={`w-5 h-5 ${theme.link}`} />
                    <span className={`font-medium ${theme.text}`}>Register Team</span>
                  </div>
                </label>

                {/* Inline Team Selection */}
                {isTeamRegistration && (
                  <div className={`ml-8 pl-4 border-l-2 ${theme.borderSecondary} space-y-3`}>
                    {teamsLoading ? (
                      <div className="flex items-center gap-x-2">
                        <div className={`w-4 h-4 border-2 ${theme.borderSecondary} border-t-transparent rounded-full animate-spin`}></div>
                        <span className={`text-sm ${theme.textMuted}`}>Loading teams...</span>
                      </div>
                    ) : userTeams.length === 0 ? (
                      <div className={`p-4 ${theme.card} rounded-lg border ${theme.border}`}>
                        <div className="text-center">
                          <Users2 className={`w-12 h-12 ${theme.textMuted} mx-auto mb-2`} />
                          <p className={`text-sm ${theme.text} mb-2`}>No teams found</p>
                          <p className={`text-xs ${theme.textMuted} mb-3`}>
                            No teams found. Create permanent or temporary teams to register.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2 justify-center">
                            <button
                              type="button"
                              onClick={handleCreatePermanentTeams}
                              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center justify-center`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Create Permanent Teams
                            </button>
                            <button
                              type="button"
                              onClick={handleCreateTemporaryTeams}
                              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center justify-center`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Create Temporary Teams
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Header row: player requirement + create buttons */}
                        <div className="flex items-center justify-between">
                          <div>
                            {(() => {
                              const req = getPlayerRequirement();
                              if (!req) return null;
                              const reqText = req.min === req.max ? `exactly ${req.min}` : `${req.min}–${req.max}`;
                              return (
                                <p className={`text-xs ${theme.textMuted}`}>
                                  Teams must have <span className="font-semibold">{reqText} player{req.max === 1 ? '' : 's'}</span>
                                </p>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleCreatePermanentTeams}
                              className={`text-xs ${theme.link} ${theme.linkHover} hover:underline flex items-center`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Permanent
                            </button>
                            <button
                              type="button"
                              onClick={handleCreateTemporaryTeams}
                              className="text-xs text-amber-500 hover:text-amber-600 hover:underline flex items-center"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Temporary
                            </button>
                          </div>
                        </div>

                        {/* Filter pills */}
                        {userTeams.some(t => t._ownerCategory === 'temporary') && (
                          <div className="flex items-center gap-2">
                            {['all', 'permanent', 'temporary'].map(f => (
                              <button
                                key={f}
                                type="button"
                                onClick={() => { setTeamFilter(f); setTeamsUiPage(1); }}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  teamFilter === f
                                    ? f === 'temporary'
                                      ? 'bg-amber-500 text-white'
                                      : 'bg-blue-500 text-white'
                                    : `${theme.backgroundSecondary} ${theme.textSecondary} border ${theme.border}`
                                }`}
                              >
                                {f === 'all'
                                  ? `All (${userTeams.length})`
                                  : f === 'permanent'
                                  ? `Permanent (${userTeams.filter(t => t._ownerCategory === 'permanent').length})`
                                  : `Temporary (${userTeams.filter(t => t._ownerCategory === 'temporary').length})`}
                              </button>
                            ))}
                          </div>
                        )}

                        {(() => {
                          const filteredTeams = userTeams.filter(t => teamFilter === 'all' || t._ownerCategory === teamFilter);
                          const pageCount = Math.max(1, Math.ceil(filteredTeams.length / TEAMS_UI_PAGE_SIZE));
                          const safePage = Math.min(teamsUiPage, pageCount);
                          const pagedTeams = filteredTeams.slice((safePage - 1) * TEAMS_UI_PAGE_SIZE, safePage * TEAMS_UI_PAGE_SIZE);
                          const canGoPrev = safePage > 1;
                          const canGoNext = safePage * TEAMS_UI_PAGE_SIZE < filteredTeams.length || teamsHasMore;

                          return (
                            <>
                              {pagedTeams.map((team) => (
                                <div
                                  key={team.id}
                                  onClick={() => canRegister && handleSelectTeam(team)}
                                  className={`p-3 border rounded-lg transition-all ${
                                    canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                                  } ${
                                    selectedTeams.some(t => t.id === team.id)
                                      ? `${theme.borderSecondary} ${theme.backgroundSecondary}`
                                      : `${theme.border} ${canRegister ? theme.cardHover : ''}`
                                  }`}
                                >
                                  <div className="flex items-center gap-x-3">
                                    <TeamLogo
                                      teamId={team.id}
                                      teamLogo={team.teamLogo || team.team_logo}
                                      teamName={team.teamName || team.name}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span
                                          className={`text-sm font-medium ${theme.link} hover:underline cursor-pointer inline-block`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/teams/${team.id}`);
                                          }}
                                        >
                                          {team.teamName || team.name}
                                        </span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                          team._ownerCategory === 'temporary'
                                            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                        }`}>
                                          {team._ownerCategory === 'temporary' ? 'Temporary' : 'Permanent'}
                                        </span>
                                        {team.isEnrolled && (
                                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                            Enrolled
                                          </span>
                                        )}
                                      </div>
                                      <p className={`text-xs ${theme.textMuted} truncate`}>
                                        {team.description || (team.maxMembers ? `Max ${team.maxMembers} members` : 'No description')}
                                      </p>
                                      {/* Player count indicator */}
                                      {(() => {
                                        const totalPlayers = team.members?.length || 0;
                                        const req = getPlayerRequirement();

                                        if (req) {
                                          const meets = totalPlayers >= req.min && totalPlayers <= req.max;
                                          const reqText = req.min === req.max ? `${req.min}` : `${req.min}–${req.max}`;
                                          return (
                                            <p className={`text-xs mt-1 font-medium ${meets ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                              {totalPlayers} / {reqText} players {meets ? '✓' : '✗'}
                                            </p>
                                          );
                                        }

                                        return (
                                          <p className={`text-xs mt-1 ${theme.textSecondary}`}>
                                            {totalPlayers} player{totalPlayers === 1 ? '' : 's'}
                                          </p>
                                        );
                                      })()}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {selectedTeams.some(t => t.id === team.id) && (
                                        <CheckCircle className={`w-4 h-4 ${theme.link}`} />
                                      )}
                                      {team._ownerCategory === 'temporary' && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteTemporaryTeam(team);
                                          }}
                                          className="p-1 text-red-400 hover:text-red-600 transition-colors"
                                          title="Delete temporary team"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {(canGoPrev || canGoNext) && (
                                <div className="flex items-center justify-between pt-1">
                                  <button
                                    type="button"
                                    disabled={!canGoPrev}
                                    onClick={() => setTeamsUiPage(p => Math.max(1, p - 1))}
                                    className={`text-xs px-3 py-1 rounded border ${theme.border} ${
                                      canGoPrev ? `${theme.link} ${theme.cardHover}` : 'opacity-40 cursor-not-allowed'
                                    }`}
                                  >
                                    Previous
                                  </button>
                                  <span className={`text-xs ${theme.textMuted}`}>
                                    {teamsLoading ? 'Loading…' : `Page ${safePage}`}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={!canGoNext || teamsLoading}
                                    onClick={async () => {
                                      if (safePage * TEAMS_UI_PAGE_SIZE >= filteredTeams.length && teamsHasMore) {
                                        await loadMoreTeams();
                                      }
                                      setTeamsUiPage(safePage + 1);
                                    }}
                                    className={`text-xs px-3 py-1 rounded border ${theme.border} ${
                                      canGoNext && !teamsLoading ? `${theme.link} ${theme.cardHover}` : 'opacity-40 cursor-not-allowed'
                                    }`}
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Individual Registration Checkbox - Always display, but Player role is disabled for team-only tournaments */}
            <div className="space-y-4">
                <label className={`flex items-center gap-x-3 ${canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                  <input
                    type="checkbox"
                    checked={isIndividualRegistration}
                    disabled={!canRegister}
                    onChange={(e) => {
                      setIsIndividualRegistration(e.target.checked);
                      if (!e.target.checked) {
                        // Clear individual roles when unchecking
                        setIndividualRoles({
                          coowner: false,
                          player: false,
                          coach: false,
                          parents: false,
                          teamOwner: false,
                          referee: false,
                          scorer: false
                        });
                      }

                      // Update registration type based on current selections
                      if (e.target.checked && isTeamRegistration) {
                        setRegistrationType('both');
                      } else if (e.target.checked) {
                        setRegistrationType('individual');
                      } else if (isTeamRegistration) {
                        setRegistrationType('team');
                      } else {
                        setRegistrationType(null);
                      }
                    }}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-x-2">
                    <User className={`w-5 h-5 ${theme.link}`} />
                    <span className={`font-medium ${theme.text}`}>Register as Individual</span>
                  </div>
                </label>

                {/* Role Selection */}
                {isIndividualRegistration && (
                  <div className={`ml-8 pl-4 border-l-2 ${theme.borderSecondary} space-y-3`}>
                    <p className={`text-sm ${theme.textSecondary}`}>Select your role(s) in this tournament:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`flex items-center gap-x-2 ${canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                        <input
                          type="checkbox"
                          checked={individualRoles.coowner}
                          disabled={!canRegister}
                          onChange={(e) => setIndividualRoles(prev => ({ ...prev, coowner: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className={`text-sm ${theme.text}`}>Organizer</span>
                      </label>
                      <label className={`flex items-center gap-x-2 ${!canRegister || !['singles', 'singles_doubles'].includes(tournament?.participantsType) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={individualRoles.player}
                          disabled={!canRegister || !['singles', 'singles_doubles'].includes(tournament?.participantsType)}
                          onChange={(e) => setIndividualRoles(prev => ({ ...prev, player: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className={`text-sm ${theme.text}`}>Player</span>
                        {!['singles', 'singles_doubles'].includes(tournament?.participantsType) && (
                          <span className={`text-xs ${theme.textMuted}`}>(Team only)</span>
                        )}
                      </label>
                      <label className={`flex items-center gap-x-2 ${canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                        <input
                          type="checkbox"
                          checked={individualRoles.coach}
                          disabled={!canRegister}
                          onChange={(e) => setIndividualRoles(prev => ({ ...prev, coach: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className={`text-sm ${theme.text}`}>Coach</span>
                      </label>
                      <label className={`flex items-center gap-x-2 ${canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                        <input
                          type="checkbox"
                          checked={individualRoles.parents}
                          disabled={!canRegister}
                          onChange={(e) => setIndividualRoles(prev => ({ ...prev, parents: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className={`text-sm ${theme.text}`}>Parents/Guardian</span>
                      </label>
                      <label className={`flex items-center gap-x-2 ${canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                        <input
                          type="checkbox"
                          checked={individualRoles.teamOwner}
                          disabled={!canRegister}
                          onChange={(e) => setIndividualRoles(prev => ({ ...prev, teamOwner: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className={`text-sm ${theme.text}`}>Team Owner</span>
                      </label>
                      <label className={`flex items-center gap-x-2 ${canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                        <input
                          type="checkbox"
                          checked={individualRoles.referee}
                          disabled={!canRegister}
                          onChange={(e) => setIndividualRoles(prev => ({ ...prev, referee: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className={`text-sm ${theme.text}`}>Referee</span>
                      </label>
                      <label className={`flex items-center gap-x-2 ${canRegister ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                        <input
                          type="checkbox"
                          checked={individualRoles.scorer}
                          disabled={!canRegister}
                          onChange={(e) => setIndividualRoles(prev => ({ ...prev, scorer: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className={`text-sm ${theme.text}`}>Scorer</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

            {/* Managed Players Registration - only for individual/singles tournaments */}
            {managedPlayers.length > 0 && ['singles', 'singles_doubles'].includes(tournament?.participantsType) && (
              <div className="space-y-3 pt-1">
                <div className="flex items-center gap-x-2">
                  <Shield className="w-5 h-5 text-blue-500" />
                  <span className={`font-medium ${theme.text}`}>Also Register as Players</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium">
                    Managed Players
                  </span>
                </div>
                <div className={`ml-7 pl-4 border-l-2 border-blue-200 dark:border-blue-800 space-y-2`}>
                  <p className={`text-xs ${theme.textMuted} mb-2`}>
                    Select players you manage to register them as individual players in this tournament.
                  </p>
                  {managedPlayersLoading ? (
                    <div className="flex items-center gap-x-2">
                      <div className={`w-4 h-4 border-2 ${theme.borderSecondary} border-t-transparent rounded-full animate-spin`} />
                      <span className={`text-sm ${theme.textMuted}`}>Loading managed players...</span>
                    </div>
                  ) : managedPlayers.map(player => {
                    const firstName = player.firstName || player.first_name || '';
                    const lastName = player.lastName || player.last_name || '';
                    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
                    const isChecked = selectedManagedPlayerIds.includes(player.id);
                    const isAlreadyRegistered = !!existingManagedPlayerRegs[player.id];
                    // A box the user just checked counts toward `isFull` itself — it must
                    // stay clickable so they can uncheck it, even though there's no room
                    // left for any *other* unchecked player.
                    const isLocked = !canRegister || (isFull && !isAlreadyRegistered && !isChecked);
                    return (
                      <label key={player.id}
                        className={`flex items-center gap-x-3 ${!isLocked ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isLocked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedManagedPlayerIds(prev => [...prev, player.id]);
                            } else {
                              setSelectedManagedPlayerIds(prev => prev.filter(id => id !== player.id));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-x-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {initials}
                          </div>
                          <span className={`text-sm ${theme.text}`}>{firstName} {lastName}</span>
                          {isAlreadyRegistered && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
                              Registered
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
          </div>
      )}

      <form className="space-y-8">
        {/* Terms and waiver — but only when this form is the end of the road.
            A priced tournament hands off to /checkout, which asks for the
            signature as its own step; asking here as well means signing twice
            for one entry, and the copy that counts is the one checkout records
            against the order. A free entry never reaches checkout, so it is
            still asked for here. */}
        {!hasFee && (tournament?.requireTerms || tournament?.requireWaiver) && (
          <TermsWaiverAgreement
            tournament={tournament}
            registrationForm={registrationForm}
            handleRegistrationFormChange={handleRegistrationFormChange}
            canRegister={canRegister}
            theme={theme}
          />
        )}

        {/* Registration fee + how they'll pay */}
        {hasFee && (
          <div className={`${theme.backgroundSecondary} rounded-lg p-6`}>
            <h4 className={`text-lg font-semibold ${theme.text} mb-2`}>Registration Fee</h4>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-2xl font-bold ${theme.text}`}>{feeLabel}</span>
              <span className={`text-sm ${theme.textSecondary}`}>
                {entryCount > 1
                  ? `${entryCount} entries × $${entryFee}`
                  : 'per entry'}
              </span>
            </div>

            {/* HOW they pay is asked at checkout, not here.
                Asking on the registration form meant two different screens
                offered payment methods with two different sets of copy, and the
                payer then went to checkout and was asked again. One place asks,
                and it is the one that takes the money. */}
            {needsPaymentChoice && (
              <p className={`mt-3 text-sm ${theme.textSecondary}`}>
                You will choose how to pay at checkout.
              </p>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-2 sm:gap-4">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`${btn.base} ${theme.buttonGhost} flex-1 px-3 py-2.5 text-sm sm:px-6 sm:py-3 sm:text-base`}
          >
            Cancel
          </button>

          {/* No Withdraw button.
              Unchecking everyone and pressing Update is the same act, done
              through the same control the payer already understands — and it
              can remove ONE person, which Withdraw could never do. A second
              button that only ever means "remove all" sat beside a list of
              checkboxes that already said who was in. */}

          <button
            type="button"
            onClick={handleSubmitRegistration}
            disabled={(!canRegister && !isRegistered) || registrationLoading
              || (!hasFee && tournament?.requireTerms  && !registrationForm.agreeToTerms)
              || (!hasFee && tournament?.requireWaiver && !registrationForm.agreeToWaiver)
              || paymentMissing}
            className={`${btn.base} ${theme.buttonPrimary} flex-1 flex items-center justify-center gap-x-1.5 sm:gap-x-2 px-3 py-2.5 text-sm sm:px-6 sm:py-3 sm:text-base`}
            title={!canRegister ? registrationDisabledReason : ''}
          >
            {registrationLoading ? (
              <>
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="whitespace-nowrap">{isRegistered ? 'Updating...' : 'Registering...'}</span>
              </>
            ) : (
              <>
                <Users2 className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span className="whitespace-nowrap">
                  {/* A button that says "Register" over a screen that then
                      asks for money has already told the payer they are done. */}
                  <span className="sm:hidden">{needsPaymentChoice ? 'Checkout' : (hasExistingEntries ? 'Update' : 'Register')}</span>
                  <span className="hidden sm:inline">
                    {needsPaymentChoice
                      ? 'Continue to checkout'
                      : (hasExistingEntries ? 'Update Registration' : 'Complete Registration')}
                  </span>
                </span>
              </>
            )}
          </button>
        </div>
      </form>

    </div>
  );
}
